import { formatInTimeZone } from 'date-fns-tz';

/**
 * Every `_at` timestamp the API returns is written by SQLite's
 * `datetime('now')`, a bare "YYYY-MM-DD HH:MM:SS" string that is UTC but
 * carries no zone marker at all, no "Z", no offset. The browser's native
 * `new Date(...)` parser treats an unmarked string like that as local time,
 * not UTC, which silently shifts every server timestamp by the viewer's UTC
 * offset. An invite sent at midnight Central was showing as "5:01 AM CDT",
 * the raw UTC clock time with a Central label stapled onto it, not an actual
 * conversion.
 *
 * `parseServerDate` fixes the input before anything touches it: a string
 * that already carries its own zone (has a "T", ends in "Z", or has a
 * +/-HH:MM offset, the shape `captured_at` is in when set client side by
 * MetOnField.jsx) is left alone; a bare SQLite string is treated as UTC.
 *
 * RTF is one team in one city, so this was originally true of every date
 * display in the app: not "whatever timezone the viewer's device happens to
 * be in", but a single company-wide America/Chicago. Superseded 2026-09-25
 * for anything tied to a real contact: every contact-related date now uses
 * that contact's own show timezone instead (formatInZone below, paired with
 * showTimezone.js's timezoneForContact), so a Texas-captured contact always
 * reads in Texas time regardless of where the viewer happens to be, not the
 * other way around. formatChicago itself is unchanged and still correct,
 * just narrower in scope than its own name suggests: only the deferred v2
 * confirmed-meeting flow (MeetingCard.jsx, AcceptMeeting.jsx, never live in
 * production) still calls it, left alone as out of scope for that reorder.
 */
export function parseServerDate(raw) {
  if (!raw) return null;
  const hasZone = /[Zz]$/.test(raw) || /[+-]\d\d:\d\d$/.test(raw) || raw.includes('T');
  const iso = hasZone ? raw : `${raw.replace(' ', 'T')}Z`;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

const CHICAGO = 'America/Chicago';

/** date-fns format(), against the real America/Chicago wall clock time, from a raw server string. Empty string for a missing/invalid input. */
export function formatChicago(raw, formatStr) {
  const date = parseServerDate(raw);
  return date ? formatInTimeZone(date, CHICAGO, formatStr) : '';
}

/**
 * Same as formatChicago, but in an arbitrary IANA zone rather than always
 * Chicago. Task: rep badge date/time, 2026-09-17, the first display in this
 * app that is not hardcoded to the company's own single timezone (see
 * web/src/lib/showTimezone.js for how that zone gets picked). Empty string
 * for a missing/invalid input, same as formatChicago.
 */
export function formatInZone(raw, timezone, formatStr) {
  const date = parseServerDate(raw);
  return date ? formatInTimeZone(date, timezone, formatStr) : '';
}
